import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, KeyRound, ScrollText, CheckCircle2 } from "lucide-react";
import {
  partnerApi, storePartnerSession, formatPartnerError,
} from "./partnerApi";
import { BRAND } from "@/lib/brandingConfig";
import "./PartnerPortal.css";

const STAGES = ["pending_password", "pending_mfa", "pending_terms", "complete"];
const STAGE_META = {
  pending_password: { idx: 1, label: "Password", icon: KeyRound },
  pending_mfa:      { idx: 2, label: "MFA",      icon: ShieldCheck },
  pending_terms:    { idx: 3, label: "Terms",    icon: ScrollText },
  complete:         { idx: 4, label: "Done",     icon: CheckCircle2 },
};

export default function PartnerAcceptInvite() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [stage, setStage] = useState("pending_password");
  const [accessToken, setAccessToken] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => { (async () => {
    if (!token) { setErr("Missing invite token."); setLoading(false); return; }
    try {
      const { data } = await partnerApi.get(
        `/partner/auth/invite-preview/${encodeURIComponent(token)}`
      );
      setPreview(data);
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setLoading(false);
    }
  })(); }, [token]);

  const setAuth = (t) => {
    setAccessToken(t);
    // Store immediately so subsequent partnerApi calls send the header.
    if (t) storePartnerSession(t, profile || { partner_user: {} });
  };

  const advanceStage = useCallback((next) => {
    setStage(next);
    if (next === "complete") {
      // Finalise profile write with fresh timestamps.
      storePartnerSession(accessToken, profile || {});
      setTimeout(() => nav("/partner/portal", { replace: true }), 900);
    }
  }, [accessToken, profile, nav]);

  if (loading) {
    return (
      <div className="hw-pp__auth-shell">
        <div className="hw-pp__auth-card" style={{ textAlign: "center" }}>
          <Loader2 className="w-6 h-6 animate-spin"
                    style={{ margin: "0 auto 0.5rem", color: "#5EEAD4" }} />
          <div className="hw-pp__auth-sub">Loading invitation…</div>
        </div>
      </div>
    );
  }

  if (err && !preview) {
    return (
      <div className="hw-pp__auth-shell">
        <div className="hw-pp__auth-card" data-testid="partner-invite-error">
          <img
            src={BRAND.logo}
            alt={BRAND.logoAlt}
            data-testid="partner-accept-invite-brand-logo"
            style={{ height: 44, width: "auto", marginBottom: "0.75rem" }}
          />
          <div className="hw-pp__eyebrow">Invitation</div>
          <h1 className="hw-pp__auth-title">Invite unavailable</h1>
          <p className="hw-pp__auth-sub">{err}</p>
          <a href="/partner/login" className="hw-pp__btn hw-pp__btn--full">Go to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="hw-pp__auth-shell" data-testid="partner-accept-invite-page">
      <div className="hw-pp__auth-card" style={{ maxWidth: 520 }}>
        <img
          src={BRAND.logo}
          alt={BRAND.logoAlt}
          data-testid="partner-accept-invite-brand-logo"
          style={{ height: 44, width: "auto", marginBottom: "0.75rem" }}
        />
        <div className="hw-pp__eyebrow">HarborWell Connect · Partner Portal Activation</div>
        <h1 className="hw-pp__auth-title">
          {stage === "complete" ? "You're all set" : "Activate your account"}
        </h1>
        <p className="hw-pp__auth-sub">
          {preview.partner_name && (<>
            {stage === "complete"
              ? <>Welcome to <strong style={{ color: "#F8FAFC" }}>{preview.partner_name}</strong>.</>
              : <>Joining <strong style={{ color: "#F8FAFC" }}>{preview.partner_name}</strong> as
                {" "}<strong style={{ color: "#5EEAD4" }}>{preview.partner_role?.toUpperCase()}</strong>.
                <br /><span style={{ color: "#CBD5E1" }}>{preview.email}</span></>
            }
          </>)}
        </p>

        <StepIndicator stage={stage} />

        {stage === "pending_password" && (
          <PasswordStep
            token={token} preview={preview}
            onSuccess={(res) => {
              setProfile(res);
              setAuth(res.access_token);
              advanceStage(res.activation_stage || "pending_mfa");
            }}
          />
        )}

        {stage === "pending_mfa" && (
          <MfaStep
            onSuccess={(nextStage) => advanceStage(nextStage || "pending_terms")}
          />
        )}

        {stage === "pending_terms" && (
          <TermsStep
            onSuccess={() => advanceStage("complete")}
          />
        )}

        {stage === "complete" && (
          <div style={{ textAlign: "center", padding: "1.5rem 0 0.5rem" }}
                data-testid="partner-invite-complete">
            <CheckCircle2 className="w-14 h-14"
                          style={{ color: "#5EEAD4", margin: "0 auto 0.5rem" }} />
            <div className="hw-pp__auth-sub">
              Redirecting to your Partner Portal…
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepIndicator({ stage }) {
  const currentIdx = STAGE_META[stage]?.idx || 1;
  return (
    <div style={{
      display: "flex", gap: "0.5rem", justifyContent: "space-between",
      margin: "1.25rem 0 1.5rem", padding: "0 0.25rem",
    }} data-testid="activation-step-indicator">
      {STAGES.map((s, i) => {
        const meta = STAGE_META[s];
        const Icon = meta.icon;
        const active = meta.idx <= currentIdx;
        const current = s === stage;
        return (
          <div key={s} style={{
            flex: 1, textAlign: "center", padding: "0.5rem 0.25rem",
            borderRadius: 12,
            background: current ? "rgba(94,234,212,0.12)" : "transparent",
            border: current ? "1px solid rgba(94,234,212,0.4)" :
              (active ? "1px solid rgba(94,234,212,0.15)" : "1px dashed #334155"),
          }} data-testid={`activation-step-${s}`}>
            <Icon className="w-4 h-4"
                    style={{ margin: "0 auto",
                              color: active ? "#5EEAD4" : "#64748B" }} />
            <div style={{ fontSize: 11, marginTop: 4,
                          color: active ? "#F8FAFC" : "#64748B",
                          fontWeight: current ? 600 : 400 }}>
              {i + 1}. {meta.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Password ───────────────────────────────────────────────────────
function PasswordStep({ token, preview, onSuccess }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    if (password.length < 12) { setErr("Password must be at least 12 characters."); return; }
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/auth/accept-invite", {
        token, password, name: name || undefined,
      });
      onSuccess(data);
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="activation-password-form">
      <label className="hw-pp__field">
        <span className="hw-pp__field-label">Your name (optional)</span>
        <input className="hw-pp__input" type="text" value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="partner-invite-name" />
      </label>
      <label className="hw-pp__field">
        <span className="hw-pp__field-label">Password (min 12 characters)</span>
        <input className="hw-pp__input" type="password" required minLength={12}
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="partner-invite-password" autoComplete="new-password" />
      </label>
      <label className="hw-pp__field">
        <span className="hw-pp__field-label">Confirm password</span>
        <input className="hw-pp__input" type="password" required minLength={12}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="partner-invite-confirm" autoComplete="new-password" />
      </label>
      <p className="hw-pp__helper" style={{ marginTop: "-0.25rem" }}>
        If you already started activation and stepped away, re-enter the same
        password you set previously to resume where you left off.
      </p>
      {err && <div className="hw-pp__error">{err}</div>}
      <button type="submit" className="hw-pp__btn hw-pp__btn--primary hw-pp__btn--full"
                disabled={busy || password.length < 12 || password !== confirm}
                data-testid="partner-invite-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Continue
      </button>
    </form>
  );
}

// ─── Step 2: MFA ────────────────────────────────────────────────────────────
function MfaStep({ onSuccess }) {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [initBusy, setInitBusy] = useState(true);

  useEffect(() => { (async () => {
    try {
      const { data } = await partnerApi.post("/partner/portal/mfa/setup/begin");
      setSetupData(data);
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setInitBusy(false);
    }
  })(); }, []);

  const verify = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/portal/mfa/setup/verify", {
        code: code.trim(),
      });
      onSuccess(data.activation_stage);
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setBusy(false);
    }
  };

  if (initBusy) {
    return (
      <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
        <Loader2 className="w-6 h-6 animate-spin"
                  style={{ margin: "0 auto 0.5rem", color: "#5EEAD4" }} />
        <div className="hw-pp__auth-sub">Generating authenticator QR…</div>
      </div>
    );
  }
  if (!setupData) return <div className="hw-pp__error">{err || "Setup failed."}</div>;

  return (
    <form onSubmit={verify} data-testid="activation-mfa-form">
      <p className="hw-pp__auth-sub" style={{ marginTop: 0 }}>
        Scan the QR code with an authenticator app (Google Authenticator,
        1Password, Authy, etc.), then enter the 6-digit code below.
      </p>
      <div style={{
        background: "#0F172A", padding: "0.75rem", borderRadius: 12,
        textAlign: "center", margin: "0.75rem 0 1rem",
      }}>
        <img src={setupData.qr_png_data_url} alt="TOTP QR"
              style={{ width: 180, height: 180, borderRadius: 8, background: "#fff" }}
              data-testid="mfa-qr-image" />
      </div>
      <details style={{ marginBottom: "0.75rem", color: "#94A3B8", fontSize: 12 }}>
        <summary style={{ cursor: "pointer" }}>Can&apos;t scan? Show manual secret</summary>
        <code data-testid="mfa-manual-secret" style={{
          display: "block", marginTop: 6, padding: 8, background: "#0F172A",
          borderRadius: 6, wordBreak: "break-all", color: "#5EEAD4",
        }}>{setupData.manual_secret}</code>
      </details>
      <label className="hw-pp__field">
        <span className="hw-pp__field-label">6-digit code</span>
        <input className="hw-pp__input" required pattern="[0-9]{6}"
                inputMode="numeric" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value)}
                data-testid="mfa-code-input" autoFocus />
      </label>
      {err && <div className="hw-pp__error">{err}</div>}
      <button type="submit" className="hw-pp__btn hw-pp__btn--primary hw-pp__btn--full"
                disabled={busy || code.length !== 6}
                data-testid="mfa-verify-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Verify &amp; continue
      </button>
      <p className="hw-pp__helper">
        Recommended: store your recovery codes after activation from
        <em> Portal &rarr; Security</em>.
      </p>
    </form>
  );
}

// ─── Step 3: Terms ──────────────────────────────────────────────────────────
function TermsStep({ onSuccess }) {
  const [docs, setDocs] = useState(null);
  const [terms, setTerms] = useState(false);
  const [priv, setPriv] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await partnerApi.get("/partner/portal/activation/status");
      setDocs(data.documents);
    } catch (e) {
      setErr(formatPartnerError(e));
    }
  })(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!terms || !priv) {
      setErr("Please accept both the Terms of Use and the Privacy Policy.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await partnerApi.post("/partner/portal/activation/accept-terms", {
        terms_accepted: true, privacy_accepted: true,
        terms_version: docs.terms.version,
        privacy_version: docs.privacy.version,
      });
      onSuccess();
    } catch (e) {
      setErr(formatPartnerError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!docs) return (
    <div style={{ textAlign: "center", padding: "1rem 0" }}>
      <Loader2 className="w-5 h-5 animate-spin"
                style={{ margin: "0 auto", color: "#5EEAD4" }} />
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="activation-terms-form">
      <p className="hw-pp__auth-sub" style={{ marginTop: 0 }}>
        Please review and accept HarborWell Connect&apos;s legal documents
        to finish activation.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem",
                     margin: "0.6rem 0 1rem" }}>
        <label className="hw-pp__checkbox-row" data-testid="terms-checkbox-row">
          <input type="checkbox" checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  data-testid="terms-checkbox" />
          <span>
            I have read and agree to the{" "}
            <a href={docs.terms.url} target="_blank" rel="noreferrer"
                data-testid="terms-link"
                style={{ color: "#5EEAD4" }}>
              {docs.terms.label}
            </a>{" "}
            <span style={{ color: "#64748B", fontSize: 11 }}>
              (v{docs.terms.version})
            </span>
          </span>
        </label>
        <label className="hw-pp__checkbox-row" data-testid="privacy-checkbox-row">
          <input type="checkbox" checked={priv}
                  onChange={(e) => setPriv(e.target.checked)}
                  data-testid="privacy-checkbox" />
          <span>
            I have read and agree to the{" "}
            <a href={docs.privacy.url} target="_blank" rel="noreferrer"
                data-testid="privacy-link"
                style={{ color: "#5EEAD4" }}>
              {docs.privacy.label}
            </a>{" "}
            <span style={{ color: "#64748B", fontSize: 11 }}>
              (v{docs.privacy.version})
            </span>
          </span>
        </label>
      </div>
      {err && <div className="hw-pp__error">{err}</div>}
      <button type="submit" className="hw-pp__btn hw-pp__btn--primary hw-pp__btn--full"
                disabled={busy || !terms || !priv}
                data-testid="accept-terms-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Accept &amp; enter portal
      </button>
      <p className="hw-pp__helper">
        By clicking Accept, your IP address and browser will be logged
        alongside your acceptance for audit purposes.
      </p>
    </form>
  );
}
